import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { FormField } from '@components/ui/form';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import { MultiSelect } from '@components/ui/form/multiSelect';
import { Checkbox } from '@components/ui/checkbox';
import { AppTooltip } from '@components/ui/tooltip/tooltip';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { useDropdownsContext } from '@contexts/dropdowns';
import { AddTaskScheduleFormValues } from '@forms/jobs/addTaskSchedule/types';
import { getCrewNames, getEquipmentNames } from '../helpers';
import { JobStageScheduleDialogSectionProps } from '../types';

export const JobStageScheduleDialogTeam = ({ popoverRef }: JobStageScheduleDialogSectionProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'pages.job.addTaskSchedule.team' });
  const { control, watch } = useFormContext<AddTaskScheduleFormValues>();
  const { fieldUsers } = useDropdownsContext();
  const foremanId = watch('foremanId');
  const foreman = fieldUsers.options.find((el) => el.id === foremanId);

  const defaults = [
    { name: 'defaultCrew' as const, items: getCrewNames(foreman?.crewMembers) },
    { name: 'defaultEquipment' as const, items: getEquipmentNames(foreman?.assetMembers) },
  ];

  return (
    <div className="flex flex-col gap-3">
      <FormField
        control={control}
        name="foremanId"
        render={({ field: { value, onChange } }) => (
          <FormItemWrapper label={t('label')} select>
            <MultiSelect
              value={value ? [value] : []}
              onChange={(v) => onChange(v[0] || '')}
              placeholder={t('placeholder')}
              disabled={fieldUsers.disabled}
              singleMode
              matchTriggerWidth
              popoverRef={popoverRef}
              options={fieldUsers.options.map((el) => ({
                label: el.fullName || el.name || '',
                value: el.id,
              }))}
            />
          </FormItemWrapper>
        )}
      />
      {!foreman && <p className="text-sm text-neutral-500">{t('noTeam')}</p>}
      {foreman &&
        defaults.map(({ name, items }) => (
          <FormField
            key={name}
            control={control}
            name={name}
            render={({ field: { value, onChange } }) => (
              <FormItemWrapper>
                <Checkbox id={name} checked={Boolean(value)} onCheckedChange={onChange}>
                  <div className="flex items-center gap-2">
                    {t(`${name}.label`)}
                    <AppTooltip
                      side="top"
                      context={
                        items.length ? (
                          <ul className="max-w-64 space-y-0.5">
                            {items.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        ) : (
                          <p>{t(`${name}.empty`)}</p>
                        )
                      }>
                      <span>
                        <SvgConsumer className="text-m text-neutral-500" id="tooltip" />
                      </span>
                    </AppTooltip>
                  </div>
                </Checkbox>
              </FormItemWrapper>
            )}
          />
        ))}
    </div>
  );
};
